import { useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { BookOpen, LogOut, Monitor, User } from 'lucide-react'
import { Avatar, AvatarImage, AvatarFallback } from 'components/ui/avatar'
import {
	DropdownMenu,
	DropdownMenuTrigger,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuSeparator,
} from 'components/ui/dropdown-menu'
import UserProfilePopup from 'components/popups/UserProfilePopup'
import FieldPrimerDialog from 'features/Help/FieldPrimerDialog'
import { logout } from 'slices/authSlice'
import routes from 'constants/routes'

/**
 * Who you are, and the two things you can do about it.
 *
 * The same menu the desktop header carries, so the field app is not a place
 * where Logout lives somewhere else. It replaces the back arrow on the home
 * screen specifically: home is the root of the field app, so there is nothing
 * behind it, and a back button there either did nothing or threw the user out.
 *
 * User Profile opens a POPUP rather than navigating. The profile route renders
 * the admin sidebar and header, so navigating to it from here would eject the
 * user into the desktop chrome they deliberately left.
 */
export function FieldAccountMenu() {
	const dispatch = useDispatch()
	const navigate = useNavigate()
	const [profileOpen, setProfileOpen] = useState(false)
	const [primerOpen, setPrimerOpen] = useState(false)

	const userInfo = useSelector((state: any) => state.userLogin?.userInfo)
	const name = userInfo?.name || userInfo?.firstName || ''
	const image = userInfo?.image

	const handleLogout = () => {
		dispatch(logout())
		navigate('/login')
	}

	return (
		<>
			<DropdownMenu>
				<DropdownMenuTrigger asChild>
					{/*
						44px of tappable area around a 32px avatar, matching the chat
						and back controls it sits beside. The avatar is the small part;
						the target is not.
					*/}
					<button
						type="button"
						aria-label={name ? `Account: ${name}` : 'Account'}
						className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-transparent p-0 focus:outline-none"
					>
						<Avatar className="h-8 w-8 border border-gray-200">
							<AvatarImage src={image} alt="" />
							<AvatarFallback className="text-xs font-semibold text-gray-500">
								{name ? name.charAt(0).toUpperCase() : '?'}
							</AvatarFallback>
						</Avatar>
					</button>
				</DropdownMenuTrigger>

				<DropdownMenuContent align="end" className="w-44">
					<DropdownMenuItem onClick={() => setProfileOpen(true)}>
						<User className="mr-2 h-4 w-4" />
						User Profile
					</DropdownMenuItem>
					{/*
						The way to the desktop site.

						On a phone `/` resolves to the field app, so the desktop home
						needs asking for by name. It lives here rather than on a screen
						because it is an account-level choice, not a thing you do to the
						page you are looking at.

						A plain link, not a stored mode. MYD-429 removed the preference
						because it could strand somebody in the wrong app; this is a
						two-way door, since HeaderCN carries the way back.
					*/}
					<DropdownMenuItem onClick={() => navigate(routes.DESKTOP_HOME)}>
						<Monitor className="mr-2 h-4 w-4" />
						Desktop site
					</DropdownMenuItem>
					{/*
						The setup primer, reachable from the phone it is about.

						Most of what it covers happens in iOS Settings rather than in
						here, so a reader is going to leave and come back. The dialog
						keeps their place for exactly that reason.
					*/}
					<DropdownMenuItem onClick={() => setPrimerOpen(true)}>
						<BookOpen className="mr-2 h-4 w-4" />
						Set up this phone
					</DropdownMenuItem>
					<DropdownMenuSeparator />
					<DropdownMenuItem onClick={handleLogout}>
						<LogOut className="mr-2 h-4 w-4" />
						Logout
					</DropdownMenuItem>
				</DropdownMenuContent>
			</DropdownMenu>

			<UserProfilePopup open={profileOpen} onClose={() => setProfileOpen(false)} />
			<FieldPrimerDialog open={primerOpen} onClose={() => setPrimerOpen(false)} />
		</>
	)
}

export default FieldAccountMenu
